import type { LocalizedDict } from '../index'

// Admin Settings → Integrations → Etsy listing push
// (features/admin/etsy-listing/EtsyListingPushSettings.tsx) and the seller's
// "Set up Pexify profiles" dialog (features/seller/etsy/PexifyProfilesDialog.tsx).
// Approved mockup 2026-10-26. "Etsy", "Pexify", "GPSR" are names.

export type EtsyListingSettingsStrings = {
  title: string
  intro: string
  tabs: { defaults: string; stores: string; pushes: string }
  scope: {
    label: string
    global: string
    custom: string
    addOrganization: string
    pickerAction: string
    pickerDescription: string
    resetToGlobal: string
  }
  shipping: {
    title: string
    hint: string
    profileName: string
    originZip: string
    usFirst: string
    usAdditional: string
    deliveryDays: string
    deliveryMax: string
    international: string
    intlOff: string
    intlFlat: string
    intlFirst: string
    intlAdditional: string
  }
  processing: {
    title: string
    hint: string
    fromCatalog: string
    manual: string
    unit: string
    days: string
    weeks: string
    manualRange: string
    manualMax: string
    readyToShip: string
  }
  quantity: {
    title: string
    hint: string
    stocked: string
    madeToOrder: string
    stockCount: string
    fixed: string
    fixedAmount: string
  }
  declaration: {
    title: string
    whoMade: string
    whoMadeOptions: { someone_else: string; i_did: string; collective: string }
    whenMade: string
    whenMadeHint: string
    whenMadeOptions: Record<string, string>
    fallbackPrice: string
    fallbackPriceSuffix: string
    bulkMargin: string
  }
  checklist: {
    title: string
    hint: string
    production_partner: string
    hs_code: string
    gpsr: string
    ai_disclosure: string
  }
  lastChanged: (when: string) => string
  futureOnly: string
  cancel: string
  save: string
  saving: string
  saved: string
  saveFailed: string
  loadError: string
  stores: {
    title: string
    hint: string
    search: string
    store: string
    permissions: string
    lastPush: string
    push: string
    switchLabel: (shop: string) => string
    ok: string
    reconnect: string
    disconnected: string
    reconnectHint: string
    empty: string
    turnedOn: (shop: string) => string
    turnedOff: (shop: string) => string
    switchFailed: string
  }
  pushes: {
    title: string
    hint: string
    when: string
    store: string
    product: string
    result: string
    empty: string
    status: Record<string, string>
  }
  dialog: {
    eyebrow: string
    title: string
    body: (shop: string) => string
    profile: string
    usFirst: string
    usAdditional: string
    delivery: string
    deliveryValue: (min: number, max: number, intl: boolean) => string
    processing: string
    processingValue: string
    permanentNote: string
    useExisting: string
    confirm: string
    working: string
    done: string
    failed: string
    panelTitle: string
    storeOff: string
    reconnect: string
    needsProfiles: string
    ready: (profile: string) => string
    readyNoPexify: (count: number) => string
    loadFailed: string
    setupButton: string
  }
}

const whenMadeEn: Record<string, string> = {
  made_to_order: 'Made to order',
  '2020_2026': '2020 – 2026',
  '2010_2019': '2010 – 2019',
  before_2007: 'Before 2007',
}

const whenMadeTr: Record<string, string> = {
  made_to_order: 'Siparişe göre üretilir',
  '2020_2026': '2020 – 2026',
  '2010_2019': '2010 – 2019',
  before_2007: '2007 öncesi',
}

export const etsyListingSettingsI18n: LocalizedDict<EtsyListingSettingsStrings> = {
  en: {
    title: 'Etsy listing push',
    intro:
      'Defaults sellers use when they send a catalog product to Etsy as a draft listing. Changes apply to future pushes only; profiles and listings already on Etsy stay as they are.',
    tabs: { defaults: 'Defaults', stores: 'Stores', pushes: 'Pushes' },
    scope: {
      label: 'Settings for',
      global: 'Everyone (default)',
      custom: 'Custom for this organization',
      addOrganization: 'Add organization…',
      pickerAction: 'Set custom values',
      pickerDescription: 'Pick the organization that gets its own Etsy listing defaults.',
      resetToGlobal: 'Use the default settings',
    },
    shipping: {
      title: 'Pexify shipping profile',
      hint: 'Created in the seller’s Etsy shop when they choose “Set up Pexify profiles”.',
      profileName: 'Profile name',
      originZip: 'Ship-from ZIP code',
      usFirst: 'US — first item',
      usAdditional: 'US — each additional item',
      deliveryDays: 'Delivery time (days)',
      deliveryMax: 'Delivery time, at most',
      international: 'Outside the US',
      intlOff: 'Off',
      intlFlat: 'Flat rate',
      intlFirst: 'Outside the US — first item',
      intlAdditional: 'Outside the US — each additional item',
    },
    processing: {
      title: 'Processing profile',
      hint: 'The preparation time Etsy requires on every listing.',
      fromCatalog: 'From the product’s production time',
      manual: 'Enter manually',
      unit: 'Unit',
      days: 'Days',
      weeks: 'Weeks',
      manualRange: 'Manual: at least – at most',
      manualMax: 'Manual, at most',
      readyToShip: 'Use “Ready to ship” for stocked products and “Made to order” for the rest',
    },
    quantity: {
      title: 'Quantity',
      hint: 'Etsy allows at most 999 per row.',
      stocked: 'Stocked product',
      madeToOrder: 'Made-to-order product',
      stockCount: 'Stock count',
      fixed: 'Fixed',
      fixedAmount: 'Fixed quantity',
    },
    declaration: {
      title: 'Product declaration and price',
      whoMade: 'Who made it',
      whoMadeOptions: {
        someone_else: 'Someone else (Pexify makes it)',
        i_did: 'The seller',
        collective: 'A collective',
      },
      whenMade: 'When it was made',
      whenMadeHint: 'The options follow Etsy’s list.',
      whenMadeOptions: whenMadeEn,
      fallbackPrice: 'When no suggested price exists',
      fallbackPriceSuffix: 'cost ×',
      bulkMargin: '“Add margin to all” default',
    },
    checklist: {
      title: '“Finish on Etsy” checklist',
      hint: 'Steps Etsy’s API can’t do; shown to the seller after the push.',
      production_partner: 'Add Pexify as a production partner',
      hs_code: 'Customs (HS) code — the product’s code with a copy button',
      gpsr: 'Product safety (GPSR) details for EU sales',
      ai_disclosure: 'AI-generated images disclosure (only when AI images were used)',
    },
    lastChanged: (when) => `Last changed: ${when}`,
    futureOnly: 'Applies to future pushes only.',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Etsy listing defaults saved',
    saveFailed: 'Couldn’t save the Etsy listing defaults',
    loadError: 'Couldn’t load the Etsy listing defaults.',
    stores: {
      title: 'Etsy listing push per store',
      hint: 'A store that is off never receives a listing. Newly connected stores start off.',
      search: 'Search stores',
      store: 'Store',
      permissions: 'Etsy permissions',
      lastPush: 'Last push',
      push: 'Listing push',
      switchLabel: (shop) => `Etsy listing push for ${shop}`,
      ok: 'OK',
      reconnect: 'Needs reconnecting',
      disconnected: 'Disconnected',
      reconnectHint:
        '“Needs reconnecting”: the store’s Etsy permissions don’t cover listing writes. Even with the switch on, pushes stop and the seller is asked to reconnect.',
      empty: 'No Etsy stores are connected yet.',
      turnedOn: (shop) => `Etsy listing push turned on for ${shop}`,
      turnedOff: (shop) => `Etsy listing push turned off for ${shop}`,
      switchFailed: 'Couldn’t change the store’s listing push',
    },
    pushes: {
      title: 'Recent pushes',
      hint: 'Each push and what happened at every step.',
      when: 'When',
      store: 'Store',
      product: 'Product',
      result: 'Result',
      empty: 'No listing has been pushed to Etsy yet.',
      status: {
        pending: 'Waiting',
        in_progress: 'Sending',
        sent: 'Draft created',
        failed: 'Failed, will retry',
        dead_letter: 'Failed',
        uncertain: 'Check Etsy',
        partial: 'Partly done',
      },
    },
    dialog: {
      eyebrow: 'Etsy shop setup',
      title: 'Set up Pexify profiles',
      body: (shop) =>
        `${shop} has no suitable shipping profile. Create these two profiles in your Etsy shop and use them for this listing?`,
      profile: 'Shipping profile',
      usFirst: 'US first item',
      usAdditional: 'Each additional item',
      delivery: 'Delivery',
      deliveryValue: (min, max, intl) => `${min}–${max} days · ${intl ? 'US and abroad' : 'US only'}`,
      processing: 'Processing time',
      processingValue: 'From the product’s production time',
      permanentNote:
        'These profiles stay in your Etsy shop; you can edit them on Etsy any time.',
      useExisting: 'I’ll pick my own profile',
      confirm: 'Set up and use',
      working: 'Setting up…',
      done: 'Pexify profiles are ready in your Etsy shop',
      failed: 'Couldn’t set up the Pexify profiles',
      panelTitle: 'Etsy shop setup',
      storeOff: 'Etsy listing push is turned off for this store. Ask Pexify to turn it on.',
      reconnect: 'Reconnect this Etsy store to allow listing writes.',
      needsProfiles: 'Your Etsy shop has no shipping profile yet.',
      ready: (profile) => `Shipping profile ready: ${profile}`,
      readyNoPexify: (count) => `${count} shipping profile(s) in your Etsy shop`,
      loadFailed: 'Couldn’t read your Etsy shop settings. Try again shortly.',
      setupButton: 'Set up Pexify profiles',
    },
  },
  tr: {
    title: 'Etsy ilan gönderimi',
    intro:
      'Satıcıların katalogdan Etsy’ye taslak ilan gönderirken kullandığı varsayılanlar. Değişiklikler yalnızca bundan sonraki gönderimleri etkiler; Etsy’de zaten açılmış profil ve ilanlar değişmez.',
    tabs: { defaults: 'Varsayılanlar', stores: 'Mağazalar', pushes: 'Gönderimler' },
    scope: {
      label: 'Ayar kapsamı',
      global: 'Genel (tüm kuruluşlar)',
      custom: 'Bu kuruluş için özel',
      addOrganization: 'Kuruluş ekle…',
      pickerAction: 'Özel değer gir',
      pickerDescription: 'Kendi Etsy ilan varsayılanları olacak kuruluşu seçin.',
      resetToGlobal: 'Genel ayara dön',
    },
    shipping: {
      title: 'Hazır kargo profili',
      hint: 'Satıcının mağazasında “Pexify profillerini kur” dediğinde açılan kargo profili.',
      profileName: 'Profil adı',
      originZip: 'Çıkış posta kodu',
      usFirst: 'ABD — ilk ürün',
      usAdditional: 'ABD — her ek ürün',
      deliveryDays: 'Teslim süresi (gün)',
      deliveryMax: 'Teslim süresi en çok',
      international: 'ABD dışına gönderim',
      intlOff: 'Kapalı',
      intlFlat: 'Sabit ücret',
      intlFirst: 'ABD dışı — ilk ürün',
      intlAdditional: 'ABD dışı — her ek ürün',
    },
    processing: {
      title: 'İşlem süresi profili',
      hint: 'Etsy’nin ilanda zorunlu tuttuğu hazırlama süresi.',
      fromCatalog: 'Katalogdaki üretim süresinden',
      manual: 'Elle gir',
      unit: 'Birim',
      days: 'Gün',
      weeks: 'Hafta',
      manualRange: 'Elle: en az – en çok',
      manualMax: 'Elle, en çok',
      readyToShip:
        'Stokta olan ürünlerde “Gönderime hazır” profili kullan, siparişle üretilenlerde “Siparişe göre üretilir”',
    },
    quantity: {
      title: 'Adet',
      hint: 'Etsy’de satır başına en çok 999.',
      stocked: 'Stoklu ürün',
      madeToOrder: 'Siparişle üretilen ürün',
      stockCount: 'Stok sayısı',
      fixed: 'Sabit',
      fixedAmount: 'Sabit adet',
    },
    declaration: {
      title: 'Ürün beyanı ve fiyat',
      whoMade: 'Kim üretti',
      whoMadeOptions: {
        someone_else: 'Başka biri (Pexify üretir)',
        i_did: 'Satıcının kendisi',
        collective: 'Ortak bir ekip',
      },
      whenMade: 'Ne zaman yapıldı',
      whenMadeHint: 'Seçenekler Etsy’nin listesini izler.',
      whenMadeOptions: whenMadeTr,
      fallbackPrice: 'Önerilen fiyat bulunamazsa',
      fallbackPriceSuffix: 'maliyet ×',
      bulkMargin: '“Hepsine kâr ekle” varsayılanı',
    },
    checklist: {
      title: '“Etsy’de tamamla” listesi',
      hint: 'API’nin yazamadığı adımlar; gönderimden sonra satıcıya gösterilir.',
      production_partner: 'Pexify’ı üretim ortağı olarak ekle',
      hs_code: 'Gümrük kodu (ürünün kodu kopyala düğmesiyle gösterilir)',
      gpsr: 'AB satışı için ürün güvenliği (GPSR) bilgileri',
      ai_disclosure: 'Yapay zekâ ile üretilmiş görsel beyanı (yalnız AI görsel kullanıldıysa)',
    },
    lastChanged: (when) => `Son değişiklik: ${when}`,
    futureOnly: 'Yalnızca bundan sonraki gönderimleri etkiler.',
    cancel: 'Vazgeç',
    save: 'Kaydet',
    saving: 'Kaydediliyor…',
    saved: 'Etsy ilan varsayılanları kaydedildi',
    saveFailed: 'Etsy ilan varsayılanları kaydedilemedi',
    loadError: 'Etsy ilan varsayılanları yüklenemedi.',
    stores: {
      title: 'Mağaza başına Etsy’ye ilan gönderimi',
      hint: 'Kapalı mağazaya hiçbir ilan gönderilmez. Yeni bağlanan mağaza kapalı başlar.',
      search: 'Mağaza ara',
      store: 'Mağaza',
      permissions: 'Etsy izinleri',
      lastPush: 'Son gönderim',
      push: 'Gönderim',
      switchLabel: (shop) => `${shop} için Etsy ilan gönderimi`,
      ok: 'Tamam',
      reconnect: 'Yeniden bağlanmalı',
      disconnected: 'Bağlı değil',
      reconnectHint:
        '“Yeniden bağlanmalı”: mağazanın Etsy izinleri ilan yazmaya yetmiyor; anahtar açılsa bile gönderim durur ve satıcıya yeniden bağlan denir.',
      empty: 'Henüz bağlı Etsy mağazası yok.',
      turnedOn: (shop) => `${shop} için Etsy ilan gönderimi açıldı`,
      turnedOff: (shop) => `${shop} için Etsy ilan gönderimi kapatıldı`,
      switchFailed: 'Mağazanın ilan gönderimi değiştirilemedi',
    },
    pushes: {
      title: 'Son gönderimler',
      hint: 'Her gönderim ve her adımda ne olduğu.',
      when: 'Zaman',
      store: 'Mağaza',
      product: 'Ürün',
      result: 'Sonuç',
      empty: 'Henüz Etsy’ye ilan gönderilmedi.',
      status: {
        pending: 'Bekliyor',
        in_progress: 'Gönderiliyor',
        sent: 'Taslak açıldı',
        failed: 'Başarısız, yeniden denenecek',
        dead_letter: 'Başarısız',
        uncertain: 'Etsy’yi kontrol et',
        partial: 'Kısmen tamamlandı',
      },
    },
    dialog: {
      eyebrow: 'Etsy mağaza hazırlığı',
      title: 'Pexify profillerini kur',
      body: (shop) =>
        `${shop} mağazanızda uygun kargo profili yok. Aşağıdaki iki profili mağazanızda açıp bu ilana bağlayalım mı?`,
      profile: 'Kargo profili',
      usFirst: 'ABD ilk ürün',
      usAdditional: 'Her ek ürün',
      delivery: 'Teslim',
      deliveryValue: (min, max, intl) => `${min}–${max} gün · ${intl ? 'ABD ve yurt dışı' : 'yalnız ABD'}`,
      processing: 'İşlem süresi',
      processingValue: 'Ürünün üretim süresinden',
      permanentNote:
        'Bu profiller Etsy mağazanızda kalıcı olarak açılır; Etsy’de istediğiniz zaman düzenleyebilirsiniz.',
      useExisting: 'Mevcut profilimi seçeyim',
      confirm: 'Kur ve bağla',
      working: 'Kuruluyor…',
      done: 'Pexify profilleri Etsy mağazanızda hazır',
      failed: 'Pexify profilleri kurulamadı',
      panelTitle: 'Etsy mağaza hazırlığı',
      storeOff: 'Bu mağazada Etsy’ye ilan gönderimi kapalı. Açılması için Pexify’a yazın.',
      reconnect: 'İlan yazabilmek için bu Etsy mağazasını yeniden bağlayın.',
      needsProfiles: 'Etsy mağazanızda henüz kargo profili yok.',
      ready: (profile) => `Kargo profili hazır: ${profile}`,
      readyNoPexify: (count) => `Etsy mağazanızda ${count} kargo profili var`,
      loadFailed: 'Etsy mağaza ayarlarınız okunamadı. Biraz sonra tekrar deneyin.',
      setupButton: 'Pexify profillerini kur',
    },
  },
}
